import { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { Job, Mutation, MutationUpdateJobArgs, VehicleInfoInput, VehicleInput } from "../generated/nest-graphql";
import { UPDATE_JOB } from "../graphql/mutations/updateJob";
import { JobStatus } from "../lib/jobStatus.utils";

// A finished or withdrawn job keeps the vehicle as it was serviced; updating it would also re-price it.
const FROZEN_STATUSES: string[] = [JobStatus.CLOSED, JobStatus.WITHDRAWN, JobStatus.WITHDRAWN_RESCHEDULE];

/** Only the job's own vehicle, on a job still in progress. */
export const shouldSyncJobVehicle = (job: Pick<Job, "vehicleId" | "status">, savedVehicleId?: string | null) =>
  !!job.vehicleId &&
  !!savedVehicleId &&
  String(job.vehicleId) === String(savedVehicleId) &&
  !FROZEN_STATUSES.includes(job.status ?? "");

/** What a vehicle save changed: the whole record (edit form) or only its mileage (Carfax prompt). */
export enum VehicleSaveScope {
  ALL = "ALL",
  MILEAGE = "MILEAGE",
}

/**
 * What the job's copy of the vehicle shows on schedule cards. The backend merges vehicleInfo field by field, so
 * symptoms, pad life and notes stay as they are. Wheel torque is left out: the job stores a number, the vehicle text.
 */
export const jobVehicleInfoFrom = (vehicle: VehicleInput): VehicleInfoInput => ({
  year: vehicle.year,
  make: vehicle.make,
  model: vehicle.model,
  vin: vehicle.vin ?? null,
  licensePlate: vehicle.licensePlate ?? null,
  licensePlateState: vehicle.licensePlateState ?? null,
  odometer: vehicle.odometer ?? null,
});

/** A mileage-only save sends only the odometer, so the job's other vehicle fields are left as they are. */
export const jobVehicleInfoFor = (vehicle: VehicleInput, scope: VehicleSaveScope): VehicleInfoInput =>
  scope === VehicleSaveScope.MILEAGE ? { odometer: vehicle.odometer ?? null } : jobVehicleInfoFrom(vehicle);

/**
 * After the vehicle record is saved, brings the job's own copy in line. Runs after the vehicle save so the
 * vehicle's odometer history is decided first. Resolves without writing when the job should not change.
 */
export const useSyncJobVehicleInfo = () => {
  const [updateJob] = useMutation<Mutation, MutationUpdateJobArgs>(UPDATE_JOB);
  return useCallback(
    async (
      job: Pick<Job, "id" | "vehicleId" | "status">,
      vehicle: VehicleInput,
      scope: VehicleSaveScope = VehicleSaveScope.ALL
    ) => {
      if (!job.id || !shouldSyncJobVehicle(job, vehicle.id)) return;
      await updateJob({
        variables: {
          id: job.id,
          updateJobInput: { vehicleId: String(job.vehicleId), vehicleInfo: jobVehicleInfoFor(vehicle, scope) },
        },
      });
    },
    [updateJob]
  );
};
